import { useMemo, Suspense, lazy } from "react";
import { AlertTriangle } from "lucide-react";
import { ChatProvider } from "@agentic_conversation/pages/chat/context";
import {
  ToolCallEntry,
  toolCallEntryFromMessage,
} from "@agentic_system/components/tool-call-entry";
import { markdownComponents } from "@agentic_conversation/pages/chat/components/markdown-components";
import { StreamingErrorBoundary } from "@agentic_conversation/pages/chat/components/error-boundary";
import { formatDate } from "@ui";
import type { LogConversationMessage } from "@agentic_system/api/logs";

const Streamdown = lazy(() =>
  import("streamdown").then((m) => ({ default: m.Streamdown }))
);

interface MessageThread {
  user: LogConversationMessage;
  synthesizer?: LogConversationMessage;
  tools: LogConversationMessage[];
}

interface ReadOnlyConversationViewProps {
  messages: LogConversationMessage[];
}

function ReadOnlySynthesizer({ message }: { message: LogConversationMessage }) {
  const isError = message.status === "error";

  return (
    <div className="flex flex-col space-y-3">
      {isError && (
        <div className="text-sm text-red-600">Erreur lors de la réponse</div>
      )}

      {/* Content - hidden when in error state (content would be a raw exception message) */}
      {message.content && !isError && (
        <div className="text-slate-600 max-w-none">
          <StreamingErrorBoundary>
            <Suspense fallback={<div className="animate-pulse">Chargement...</div>}>
              <Streamdown
                components={markdownComponents}
                isAnimating={false}
                controls={{ code: true, table: false }}
              >
                {message.content}
              </Streamdown>
            </Suspense>
          </StreamingErrorBoundary>
        </div>
      )}

      <div className="flex items-center justify-end">
        <div className="text-[11px] text-slate-400">
          {formatDate(message.created_at)} (#{message.id})
        </div>
      </div>
    </div>
  );
}

function ReadOnlyUserThread({ thread }: { thread: MessageThread }) {
  const { user, synthesizer, tools } = thread;
  const isInterrupted = synthesizer?.status === "interrupted";

  return (
    <div className="flex flex-col space-y-4">
      {/* User question */}
      <div className="w-4/5 bg-default-50 p-4 rounded-lg ring-1 ring-default-200 shadow-lg shadow-default flex flex-col space-y-4">
        <div className="flex flex-col space-y-2">
          <div className="text-xs font-medium uppercase text-default-500">
            Vous
          </div>
          <div className="text-default-600 font-medium">{user.content}</div>
        </div>
        <div className="text-[11px] text-slate-400">
          {formatDate(user.created_at)} (#{user.id})
        </div>
      </div>

      {/* AI response */}
      <div className="flex">
        <div className="bg-white ring-1 ring-slate-300 w-full rounded-lg text-slate-600 overflow-hidden flex flex-col space-y-6 p-6 min-w-0">
          {/* Tools */}
          {tools.length > 0 && (
            <div className="w-full min-w-0">
              {tools.map((tool, index) => (
                <ToolCallEntry
                  key={tool.id}
                  entry={toolCallEntryFromMessage(tool)}
                  isLast={index === tools.length - 1}
                />
              ))}
            </div>
          )}

          {/* Synthesizer response */}
          {synthesizer && !isInterrupted && (
            <ReadOnlySynthesizer message={synthesizer} />
          )}

          {/* Interrupted state - static badge instead of interactive InterruptedFooter */}
          {isInterrupted && (
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <span className="text-sm text-amber-700">Interrompu</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ReadOnlyConversationView({ messages }: ReadOnlyConversationViewProps) {
  const threads = useMemo((): MessageThread[] => {
    const userMessages = messages.filter((m) => m.template_type === "user");

    return userMessages.map((user) => {
      const replies = messages.filter((m) => m.parent_id === user.id);
      return {
        user,
        synthesizer: replies.find((r) => r.template_type === "synthesizer"),
        tools: replies.filter((r) => r.template_type === "tool"),
      };
    });
  }, [messages]);

  if (threads.length === 0) return null;

  // Wrap in a no-op ChatProvider so follow-up markdown components don't crash
  const noop = () => {};

  return (
    <ChatProvider sendUserMessageFromSuggestion={noop}>
      <div className="flex flex-col space-y-6">
        {threads.map((thread) => (
          <ReadOnlyUserThread key={thread.user.id} thread={thread} />
        ))}
      </div>
    </ChatProvider>
  );
}
